// The question a person attached to several Registrars answers before any page
// shows them data: which Registrar they are working for.
//
// per docs/shape-ops-tenant-selector.md §7 Revision 2026-10-13 (T168) and §9
// (T174). Register entry UC11-05 (CF-27): the user associated with several
// Registrars selects the active one, and data, filters, reporting and
// credentials are limited to it; caiet NC-UC50-01: the Registrar is selected
// after authentication. The selection is the server session's, so a new
// session asks again whatever this browser held before. The shell
// wraps its workspace outlet in it, above RouteGuard, so the rail stays drawn
// and a deep link is decided by the Registrar chosen rather than sent home for
// the lack of one.
import { useId, type ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudInfoBox } from '@egov-moldova/mud-react';
import { PageHeader } from '@simsm/ui-shared';
import { isReporter } from '../../app/access-model';
import { useActiveRegistrar } from '../../hooks/use-active-registrar';
import { useSession } from '../../hooks/use-session';
import './registrar-selector.css';

export function ActiveRegistrarGate({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslate();
  const session = useSession().data;
  const { options, awaitingChoice, choose, choosing, chooseErrorKey } = useActiveRegistrar();
  const listLabelId = useId();

  if (!session || !isReporter(session.activeRole) || !awaitingChoice) return <>{children}</>;

  return (
    <section className="simsm-registrar-gate">
      <PageHeader title={t('registrar.gate.title')} lede={t('registrar.gate.lede')} />
      <h2 id={listLabelId} className="simsm-registrar-gate__list-label">
        {t('registrar.gate.list_label')}
      </h2>
      <ul className="simsm-registrar-gate__list" aria-labelledby={listLabelId}>
        {options.map((option) => {
          // D5's fallback: an IDNO the catalog could not resolve is already the
          // name, and is printed once.
          const named = option.name !== option.idno;
          return (
            <li key={option.idno} className="simsm-registrar-gate__option">
              <span className="simsm-registrar-gate__text">
                <span className="simsm-registrar-gate__name">{option.name}</span>
                {named ? (
                  <span className="simsm-registrar-gate__idno">
                    {t('registrar.gate.idno', { idno: option.idno })}
                  </span>
                ) : null}
              </span>
              {/* AGE's button clips its label to one line, so the name is page
                  text beside it and the accessible name carries both parts. */}
              <MudButton
                shape="circular"
                variant="strict"
                appearance="outlined"
                size="md"
                label={t('registrar.gate.choose_aria', { name: option.name, idno: option.idno })}
                disabled={choosing !== null}
                onClick={() => choose(option.idno)}
              >
                {t('registrar.gate.choose')}
              </MudButton>
            </li>
          );
        })}
      </ul>
      {chooseErrorKey ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t(chooseErrorKey)}
        </MudInfoBox>
      ) : null}
      <p className="simsm-registrar-gate__hint">{t('registrar.gate.hint')}</p>
    </section>
  );
}
